// Dashboard layout. Sidebar + main content share the canvas (Parchment).
// No border separator between them — the break is implicit via typography
// per the design brief. Active nav items get a small Teal dot indicator
// next to the label; inactive items render in text-secondary, active in
// text-primary.
//
// Section labels (My Work / CRM / Service / Settings) are text-tertiary
// uppercase tracking-wide. Phase 1.1 only ships the items that already
// have a destination route; the rest land alongside their respective
// chunks.
//
// The sidebar is collapsible. Collapsed state shrinks the column to an
// icon rail (64px) and hides labels / section headings / entity pill /
// user name; the toggle persists across reloads via localStorage so the
// user only picks once.

import {
  CheckSquare,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  PlayCircle,
  Settings,
  Shield,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { useAuth, usePermissions, type PermissionFlags } from "@/features/auth";
import {
  BeneAIDrawer,
  BeneAIDrawerProvider,
  BeneAILauncher,
} from "@/features/beneai";
import {
  CommitmentModalHost,
  CommitmentModalProvider,
} from "@/features/commitments";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  // Settings-03 — when set, the item is only rendered for users
  // whose ``PermissionFlags`` have this flag set to ``true``.
  // Operational nav items (Dashboard, Accounts, Commitment Tracker,
  // Playbooks) intentionally omit this field so every active
  // membership keeps access.
  permission?: keyof PermissionFlags;
  // Marketing-side gate: render with a muted "Coming soon" pill and
  // suppress navigation from the sidebar. The route itself remains
  // reachable (deep links, ⌘+K, dev pokes) — this only hides the
  // entry point from non-internal users.
  comingSoon?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// BeneAI is *not* a regular nav item — it opens the drawer rather
// than navigating away. The launcher component is rendered inline by
// ``DashboardLayout`` next to the Dashboard entry below, so it
// reserves the same visual slot in the sidebar without piggybacking
// on the NavLink machinery.
const NAV_SECTIONS: NavSection[] = [
  {
    label: "My work",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/dashboard/commitment-tracker", label: "Commitment Tracker", icon: CheckSquare },
    ],
  },
  {
    label: "Growth",
    items: [
      {
        to: "/dashboard/sales-crm",
        label: "Sales CRM",
        icon: TrendingUp,
        permission: "can_access_sales_crm",
      },
    ],
  },
  {
    label: "OPS CRM",
    items: [
      { to: "/dashboard/accounts", label: "Accounts", icon: Users },
      // FE-SCRM-030: Carriers is operational infrastructure (producers
      // look up reps, AMs reference contacts on stewardship calls), so
      // it lives one click away in the CRM section — not hidden under
      // Settings. Read access is open to every authenticated member;
      // the in-page write actions gate on ``is_admin_or_owner``.
      { to: "/dashboard/carriers", label: "Carriers", icon: Shield },
      { to: "/dashboard/playbooks", label: "Playbooks", icon: PlayCircle },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/dashboard/settings", label: "Settings", icon: Settings },
    ],
  },
  // Files / Touchpoints / Documents / Plan Years / Activity live
  // *inside* the Account context, not at the top level. Don't add
  // them back here without revisiting the product architecture.
];


function filterByPermissions(
  sections: NavSection[], flags: PermissionFlags,
): NavSection[] {
  // Drop items whose permission flag is false. Sections that lose
  // every item collapse out entirely so we don't render an empty
  // header (e.g. Growth disappears for plain members).
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.permission) return true;
        return Boolean(flags[item.permission]);
      }),
    }))
    .filter((section) => section.items.length > 0);
}

const SECONDARY_ITEMS: NavItem[] = [];


const COLLAPSED_STORAGE_KEY = "benecrm:sidebar-collapsed";


function readCollapsedFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}


function NavItemLink({
  item, collapsed,
}: { item: NavItem; collapsed: boolean }) {
  if (item.comingSoon) {
    return (
      <div
        data-nav-coming-soon="true"
        title={collapsed ? `${item.label} — coming soon` : undefined}
        aria-disabled="true"
        className={[
          "relative flex items-center rounded-md py-2 text-sm",
          "cursor-not-allowed text-text-tertiary opacity-60",
          collapsed ? "justify-center px-2" : "gap-2.5 px-3",
        ].join(" ")}
      >
        <span aria-hidden="true" className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full bg-transparent" />
        {!collapsed && (
          <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-transparent" />
        )}
        <item.icon
          size={collapsed ? 18 : 16}
          className="text-text-tertiary"
        />
        {!collapsed && (
          <span className="flex flex-col leading-tight">
            <span>{item.label}</span>
            <span
              data-testid="nav-coming-soon-badge"
              className="text-[10px] font-normal italic text-text-tertiary"
            >
              coming soon
            </span>
          </span>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        [
          // No solid pill bg — the active treatment is a small
          // Teal dot indicator + a 2px left-edge accent + heavier
          // label weight. App.test still asserts no
          // ``bg-(sage|recessed|raised|teal)-tint`` on the link.
          "relative flex items-center rounded-md py-2 text-sm transition-fast ease-out",
          collapsed ? "justify-center px-2" : "gap-2.5 px-3",
          isActive
            ? "font-semibold text-text-primary"
            : "text-text-secondary hover:text-text-primary",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          {/* Left-edge accent — 2px Teal stripe for the active item
              that reads at a glance without crossing into pill-bg
              territory. Inactive items render an invisible spacer
              so the label x-offset stays uniform. */}
          <span
            aria-hidden="true"
            className={[
              "absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full transition-fast ease-out",
              isActive ? "bg-teal" : "bg-transparent",
            ].join(" ")}
          />
          {!collapsed && (
            <span
              data-active-indicator={isActive ? "true" : "false"}
              aria-hidden="true"
              className={[
                "inline-block h-1.5 w-1.5 rounded-full transition-fast ease-out",
                isActive ? "bg-teal" : "bg-transparent",
              ].join(" ")}
            />
          )}
          <item.icon
            size={collapsed ? 18 : 16}
            className={isActive ? "text-text-primary" : "text-text-tertiary"}
          />
          {!collapsed && <span>{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

function computeInitials(name: string): string {
  const cleaned = (name ?? "").trim();
  if (cleaned === "") return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    // Email-ish or single token — first two characters.
    return cleaned.slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-5 pb-1 text-xs font-medium uppercase tracking-wide text-text-tertiary">
      {children}
    </div>
  );
}


function CollapsedSectionDivider() {
  // Visual breath between sections in the icon rail; the uppercase
  // label is hidden when collapsed.
  return (
    <div
      aria-hidden="true"
      className="mx-3 my-2 h-px bg-border-subtle/60"
    />
  );
}


export function DashboardLayout() {
  const auth = useAuth();
  const permissions = usePermissions();
  const navSections = filterByPermissions(NAV_SECTIONS, permissions);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => readCollapsedFromStorage(),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        COLLAPSED_STORAGE_KEY, String(collapsed),
      );
    } catch {
      // localStorage can be denied (private mode, embedded webviews);
      // the toggle still works for the current session.
    }
  }, [collapsed]);

  return (
    <CommitmentModalProvider>
      <BeneAIDrawerProvider>
      <div className="flex h-full bg-canvas text-text-primary">
      {/* Sidebar — same Parchment as main; no border separator. */}
      <aside
        data-testid="dashboard-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={[
          "flex flex-col bg-canvas py-5 transition-fast ease-out",
          collapsed ? "w-16 px-2" : "w-60 px-3",
        ].join(" ")}
      >
        {/* Brand lockup + toggle. Expanded shows the wordmark; the
            toggle sits in the top-right. Collapsed hides the
            wordmark, centers the symbol, and the toggle drops below
            it so the rail isn't lopsided. Symbol asset already
            exists at /brand/benecrm-logo-symbol.png so no design
            asset work is needed. */}
        <div
          className={[
            "flex items-center",
            collapsed
              ? "flex-col gap-2 px-1"
              : "justify-between px-3 pb-1",
          ].join(" ")}
        >
          <img
            src={
              collapsed
                ? "/brand/benecrm-logo-symbol.png"
                : "/brand/benecrm-logo.png"
            }
            alt="BeneCRM"
            className={collapsed ? "h-7 w-7" : "h-7 w-auto"}
          />
          <button
            type="button"
            data-testid="sidebar-collapse-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((prev) => !prev)}
            className="rounded p-1 text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
          >
            {collapsed ? (
              <PanelLeftOpen size={16} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={16} aria-hidden="true" />
            )}
          </button>
        </div>

        {auth.currentMembership && !collapsed && (
          <div
            data-testid="active-entity-pill"
            className="mx-3 mt-3 inline-flex items-center gap-1 rounded-md bg-recessed px-2.5 py-1 text-xs"
          >
            <span className="font-medium text-text-primary">
              {auth.currentMembership.entity.name}
            </span>
          </div>
        )}

        {/* Nav sections */}
        <nav className="mt-4 flex flex-1 flex-col">
          {navSections.map((section, idx) => (
            <div key={section.label} className="space-y-0.5">
              {collapsed
                ? idx > 0 && <CollapsedSectionDivider />
                : <SectionLabel>{section.label}</SectionLabel>}
              {section.items.flatMap((item, itemIdx) => {
                const nodes = [
                  <NavItemLink
                    key={item.to} item={item} collapsed={collapsed}
                  />,
                ];
                if (section.label === "My work" && itemIdx === 0) {
                  // BeneAI launcher slots in just after Dashboard. It
                  // is a button, not a NavLink — clicking it opens the
                  // right-side drawer rather than navigating.
                  nodes.push(
                    <BeneAILauncher
                      key="beneai-launcher"
                      collapsed={collapsed}
                    />,
                  );
                }
                return nodes;
              })}
            </div>
          ))}

          {/* Secondary nav (Settings, etc.) anchored at the bottom */}
          <div className="mt-auto space-y-0.5 pt-4">
            {SECONDARY_ITEMS.map((item) => (
              <NavItemLink
                key={item.to} item={item} collapsed={collapsed}
              />
            ))}
            <button
              type="button"
              data-testid="logout-button"
              title={collapsed ? "Sign out" : undefined}
              aria-label={collapsed ? "Sign out" : undefined}
              onClick={async () => {
                await auth.logout();
                navigate("/login", { replace: true });
              }}
              className={[
                "flex w-full items-center rounded-md py-2 text-sm text-text-secondary transition-fast ease-out hover:text-text-primary",
                collapsed ? "justify-center px-2" : "gap-2.5 px-3",
              ].join(" ")}
            >
              {!collapsed && (
                <span
                  aria-hidden="true"
                  className="inline-block h-1.5 w-1.5 rounded-full bg-transparent"
                />
              )}
              <LogOut
                size={collapsed ? 18 : 16}
                className="text-text-tertiary"
                aria-hidden="true"
              />
              {!collapsed && <span>Sign out</span>}
            </button>
          </div>
        </nav>

        {/* Bottom identity pill — Pencil places the user here, not
            the entity. Avatar circle + display name + role; the
            entity context lives in the top pill so the two
            identities don't fight. Email + git sha collapse into
            the same row as a small caption. When collapsed, the
            pill shrinks to just the avatar (with the role available
            via tooltip). */}
        {auth.user && (
          <div
            data-testid="sidebar-user-pill"
            className={[
              "mt-3 flex items-center rounded-md py-2",
              collapsed ? "justify-center px-1" : "gap-2 px-2",
            ].join(" ")}
            title={
              collapsed
                ? `${auth.user.display_name || auth.user.email}${
                    auth.currentMembership
                    && auth.currentMembership.roles.length > 0
                      ? ` · ${auth.currentMembership.roles.join(", ")}`
                      : ""
                  }`
                : undefined
            }
          >
            <span
              aria-hidden="true"
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sage-tint text-[11px] font-semibold text-teal"
            >
              {computeInitials(
                auth.user.display_name ?? auth.user.email,
              )}
            </span>
            {!collapsed && (
              <div className="flex min-w-0 flex-col leading-tight">
                <span
                  data-testid="sidebar-user-name"
                  className="truncate text-xs font-medium text-text-primary"
                >
                  {auth.user.display_name || auth.user.email}
                </span>
                <span
                  data-testid="sidebar-user-role"
                  className="truncate text-[11px] text-text-tertiary"
                >
                  {auth.currentMembership
                    && auth.currentMembership.roles.length > 0
                    ? auth.currentMembership.roles.join(", ")
                    : "—"}
                </span>
              </div>
            )}
          </div>
        )}
        {!collapsed && auth.user && (
          <span
            data-testid="current-user-email"
            className="hidden"
            aria-hidden="true"
          >
            {auth.user.email}
          </span>
        )}
      </aside>

      {/* Main column — same canvas, no left border. The layout no
          longer renders its own page-title chrome; each page owns
          its own h1 (Commitment Tracker, Accounts, Account detail,
          etc.) so the layout doesn't double-stamp the heading and
          the FE-002 placeholder banner is gone. */}
      {/* ``min-w-0`` on the main column + ``overflow-x-hidden`` on
          ``<main>`` keep horizontal overflow scoped to whichever
          surface owns its own scroll container (the kanban board's
          ``overflow-x-auto``, the wide tables under
          ``overflow-x-auto`` wrappers). Without these the page
          itself stretches and the page-level scrollbar swallows
          the kanban's own scroll. */}
      <div className="flex min-w-0 flex-1 flex-col bg-canvas">
        <main
          data-testid="dashboard-main"
          // Standard scroll container. Pages that need to fill the
          // viewport (e.g. the Commitment Tracker board) build
          // their own flex column inside this main and use
          // ``h-full`` to inherit the height.
          className="flex-1 overflow-x-hidden overflow-y-auto bg-canvas px-8 py-6"
        >
          <Outlet />
        </main>
      </div>
      </div>
      <CommitmentModalHost />
      <BeneAIDrawer />
      </BeneAIDrawerProvider>
    </CommitmentModalProvider>
  );
}
